import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tag, Search, Plus, MoreHorizontal, Pencil, Trash2, Copy, Check } from 'lucide-react';
import { adminApi } from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';
import { PageHeader, PageContent, Tabs, EmptyState, LoadingState, Button, Table, AlertDialog, StatusBadge } from '@printtat/ui';
import type { Column, SortState } from '@printtat/ui';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printtat/ui';
import { DISCOUNT_TYPE_LABELS } from '@printtat/shared';
import type { DiscountCodeListItem, DiscountCode, DiscountType } from '@printtat/shared';
import { DiscountCodeModal } from '@/components/discounts/DiscountCodeModal';

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DISCOUNT_TYPE_COLORS: Record<string, 'blue' | 'green' | 'purple'> = {
  percentage: 'blue', fixed_amount: 'green', free_shipping: 'purple',
};

const getDiscountStatus = (item: DiscountCodeListItem): { label: string; color: 'gray' | 'red' | 'amber' | 'yellow' | 'green' } => {
  const now = new Date();
  const expired = item.expiresAt && new Date(item.expiresAt) < now;
  const notStarted = item.startsAt && new Date(item.startsAt) > now;
  const exhausted = item.maxUses !== null && item.usedCount >= item.maxUses;

  if (!item.isActive) return { label: 'Inactive', color: 'gray' };
  if (expired) return { label: 'Expired', color: 'red' };
  if (exhausted) return { label: 'Exhausted', color: 'amber' };
  if (notStarted) return { label: 'Scheduled', color: 'yellow' };
  return { label: 'Active', color: 'green' };
};

const formatValue = (item: DiscountCodeListItem) => {
  if (item.type === 'percentage') {
    const cap = item.maxDiscountAmount != null ? ` (max ${item.maxDiscountAmount} EGP)` : '';
    return `${item.value}%${cap}`;
  }
  if (item.type === 'fixed_amount') return `${item.value} EGP`;
  return '—';
};

const TAB_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

export const AdminDiscountsPage = () => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ key: 'createdAt', order: 'desc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<DiscountCode | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const limit = 20;

  const isActiveFilter = tab === 'active' ? true : tab === 'inactive' ? false : undefined;

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.discounts.all(), { search, isActive: isActiveFilter, page, limit, ...sort }],
    queryFn: () => adminApi.discounts.list({
      search: search || undefined,
      isActive: isActiveFilter,
      page,
      limit,
      sortBy: sort.key,
      sortOrder: sort.order,
    }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => adminApi.discounts.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.discounts.all() });
      setModalOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminApi.discounts.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.discounts.all() });
      setEditingDiscount(null);
      setModalOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.discounts.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.discounts.all() });
      setDeletingId(null);
    },
  });

  const handleOpenCreate = () => {
    setEditingDiscount(null);
    setModalOpen(true);
  };

  const handleOpenEdit = async (id: string) => {
    const detail = await adminApi.discounts.detail(id);
    setEditingDiscount(detail);
    setModalOpen(true);
  };

  const handleSave = (data: Record<string, unknown>) => {
    if (editingDiscount) {
      updateMutation.mutate({ id: editingDiscount.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const discounts = data?.discounts ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const columns: Column<DiscountCodeListItem>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      render: (d) => (
        <span className="inline-flex items-center gap-1.5 group">
          <span className="text-sm font-mono font-medium text-gray-900">{d.code}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(d.code);
              setCopiedCode(d.code);
              setTimeout(() => setCopiedCode(null), 1500);
            }}
            className="p-0.5 rounded text-gray-300 hover:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {copiedCode === d.code ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
          </button>
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (d) => <StatusBadge label={DISCOUNT_TYPE_LABELS[d.type as DiscountType] ?? d.type} color={DISCOUNT_TYPE_COLORS[d.type] ?? 'gray'} size="xs" />,
    },
    {
      key: 'value',
      header: 'Value',
      render: (d) => <span className="text-sm text-gray-700">{formatValue(d)}</span>,
    },
    {
      key: 'usedCount',
      header: 'Usage',
      sortable: true,
      render: (d) => (
        <span className="text-sm text-gray-600">
          {d.usedCount}{d.maxUses !== null ? ` / ${d.maxUses}` : ''}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (d) => { const s = getDiscountStatus(d); return <StatusBadge label={s.label} color={s.color} size="xs" />; },
    },
    {
      key: 'expiresAt',
      header: 'Expires',
      render: (d) => <span className="text-sm text-gray-500">{formatDate(d.expiresAt)}</span>,
    },
    {
      key: 'createdBy',
      header: 'Created by',
      render: (d) => <span className="text-sm text-gray-500">{d.createdByEmail ?? '—'}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (d) => <span className="text-sm text-gray-500">{formatDate(d.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      className: 'w-10',
      render: (d) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={16} className="text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleOpenEdit(d.id)}>
              <Pencil size={14} className="mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => setDeletingId(d.id)}
            >
              <Trash2 size={14} className="mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PageContent className="overflow-y-auto h-screen">
      <PageHeader
        icon={Tag}
        title="Discounts"
        description={`${total} discount code${total !== 1 ? 's' : ''}`}
        actions={
          <Button onClick={handleOpenCreate}>
            <Plus size={16} className="mr-1.5" />
            Create code
          </Button>
        }
      />

      {/* Tabs + search */}
      <div className="mt-6 flex items-center gap-3">
        <Tabs
          tabs={TAB_OPTIONS}
          value={tab}
          onChange={(v) => { setTab(v); setPage(1); }}
          variant="pill"
        />
        <div className="relative flex-1 max-w-sm ml-auto">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
      </div>

      {/* Table */}
      <div className="mt-6">
        {isLoading ? (
          <LoadingState variant="skeleton" columns={1} rows={5} />
        ) : discounts.length === 0 ? (
          <EmptyState
            icon={Tag}
            title="No discount codes"
            description={search || tab !== 'all' ? 'Try adjusting your filters' : 'Create your first discount code'}
          />
        ) : (
          <>
            <Table
              columns={columns}
              data={discounts}
              keyExtractor={(d) => d.id}
              sort={sort}
              onSortChange={(s) => { setSort(s); setPage(1); }}
            />

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit modal */}
      <DiscountCodeModal
        discount={editingDiscount}
        open={modalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setModalOpen(false);
            setEditingDiscount(null);
          }
        }}
        onSave={handleSave}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deletingId}
        onOpenChange={(open) => { if (!open) setDeletingId(null); }}
        title="Delete discount code"
        description="This will permanently delete this discount code. This action cannot be undone."
        confirmText="Delete"
        onConfirm={() => { if (deletingId) deleteMutation.mutate(deletingId); }}
        variant="destructive"
      />
    </PageContent>
  );
};
